import React from "react";
import {
  FiMic,
  FiNavigation,
  FiHeadphones,
  FiSmartphone,
  FiCpu,
} from "react-icons/fi";
import { MdOutlineRecordVoiceOver } from "react-icons/md";
import AIAutomationTemplate from "./AIAutomationTemplate";

const VoiceBotAssistant = () => {
  const features = [
    {
      title: "Natural Speech AI",
      description:
        "Neural voices with human-like prosody deliver brand-consistent conversations across industries.",
      icon: <FiMic />,
    },
    {
      title: "Voice Navigation",
      description:
        "Guide customers through IVRs, kiosks, and in-app journeys using context-aware voice commands.",
      icon: <FiNavigation />,
    },
    {
      title: "Support Automation",
      description:
        "Voice bots authenticate users, answer queries, and escalate to agents with full conversation context.",
      icon: <FiHeadphones />,
    },
    {
      title: "Voice UX Systems",
      description:
        "Design sonic identities, dialog flows, and error recovery patterns that feel natural and inclusive.",
      icon: <MdOutlineRecordVoiceOver />,
    },
    {
      title: "Omnichannel Continuity",
      description:
        "Switch seamlessly between voice, chat, and SMS while carrying context, transcripts, and intents.",
      icon: <FiSmartphone />,
    },
    {
      title: "Real-Time Intelligence",
      description:
        "Transcription, sentiment detection, and analytics enable coaches to monitor and optimize performance.",
      icon: <FiCpu />,
    },
  ];

  const steps = [
    {
      title: "Experience Strategy",
      description:
        "Define target personas, sonic branding, and KPIs for the ideal voice experience.",
    },
    {
      title: "Conversation & UX Design",
      description:
        "Build detailed call flows, prompts, and recovery logic with accessibility specialists.",
    },
    {
      title: "Platform & Integration Build",
      description:
        "Implement speech recognition, NLU, and telephony integrations with CRMs or ticketing systems.",
    },
    {
      title: "Optimization & Scaling",
      description:
        "Continuously tune prompts, measure CSAT, and expand use cases across languages and lines of business.",
    },
  ];

  const benefits = [
    "Reduce handle time with precise, intelligent voice interactions",
    "Improve CX by eliminating DTMF-heavy experiences",
    "Maintain consistency across IVR, kiosk, and in-app journeys",
    "Capture insights via real-time transcription and analytics",
    "Boost accessibility with multilingual and inclusive design",
    "Scale support capacity without expanding agent headcount",
  ];

  const expertise = [
    "Speech scientists and UX writers specializing in multimodal design",
    "Deployments across Twilio Voice, Amazon Connect, Genesys, and custom SIP stacks",
    "Real-time analytics dashboards for QA supervisors and CX leads",
    "Deep integration experience with CRM, billing, and policy systems",
    "Security-first design using voice biometrics and consent capture",
    "Localization teams covering 25+ languages and dialects",
  ];

   const image = [
    {
      side_image: "../assets/images/ai-automation/8.webp",
    }

  ];

  const useCases = [
    {
      industry: "Automotive",
      title: "In-Dealer Voice Concierge",
      description:
        "Voice bot schedules service, orders parts, and updates CRM automatically.",
    },
    {
      industry: "Airlines",
      title: "Flight Disruption Assistant",
      description:
        "Handles rebooking, vouchers, and notifications during irregular operations.",
    },
    {
      industry: "Utilities",
      title: "Outage Response Bot",
      description:
        "Captures outage reports, shares restoration ETAs, and triggers field notifications.",
    },
    {
      industry: "Banking",
      title: "Secure Voice Banking",
      description:
        "Authenticates via voice biometrics, shares balances, and blocks cards in seconds.",
    },
  ];

  const reasons = [
    "Voice-first studio blending conversation design, telephony, and AI",
    "Ready-made accelerators for IVR modernization and virtual agents",
    "Compliance-ready deployments for PCI, HIPAA, and regional regs",
    "Continuous testing lab for latency, accuracy, and experience scoring",
    "Dedicated optimization squad tracking KPIs and user feedback",
    "Flexible engagement spanning innovation sprints to managed service",
  ];

  return (
    <AIAutomationTemplate
      title="Voice Bot Assistant"
      heroDescription="Give customers intuitive voice experiences powered by natural speech AI, omnichannel context, and deep systems integration."
      about={{
        heading: "About Voice Bot Assistant Services",
        description:
          "We design and deploy intelligent voice systems that automate support, guide experiences, and capture insights in real time.",
      }}
      features={features}
      steps={steps}
      benefits={benefits}
      expertise={expertise}
      image={image}
      useCases={useCases}
      reasons={reasons}
      ctaLabel="Launch a Voice Bot"
      meta={{
        title: "Voice Bot Assistant | Conversational Voice AI | Ayanshtech",
        description:
          "Natural speech AI with voice navigation, support automation, and voice UX systems.",
        ogTitle: "Voice Bot Assistant Solutions",
        ogDescription:
          "Create voice bot assistants with natural speech AI, omnichannel continuity, and real-time intelligence.",
        ogUrl: "http://ayanshtechnology.com/voice-bot-assistant",
        canonical: "http://ayanshtechnology.com/voice-bot-assistant",
      }}
    />
  );
};

export default VoiceBotAssistant;


